import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/modern/components/ui/carousel";
import { Button } from "@/modern/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/modern/components/ui/dropdown-menu";
import { useConfirm } from "@omit/react-confirm-dialog";
import { MoreVertical, Eye, Trash2, Download, Pencil, Award } from "lucide-react";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/modern/components/ui/tooltip";
import FilePreview from "@/modern/components/file-preview";
import { useEntityFileContext } from "../context/EntityFileContext";
import { Skeleton } from "@/modern/components/ui/skeleton";
import { useState } from "react";
import ImageAnnotationEditor from "@/react/components/entityViews/files/components/editors/ImageAnnotationEditor";
import { FormattedFile } from "../context/EntityFileContext";
import { isImageFile } from "@/other/utils/utils.jsx";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import { useFileSpotIncentive } from "@/react/components/entityViews/files/hooks/useFileSpotIncentive";
import { cn } from "@/modern/utils";

export const FileSliderView = () => {
  const { boardFiles, companyCamPhotos, handleFileOpen, handleFileDownload, deleteFile, fetchCompanyCamPhotos, hasMorePhotos, isFetchingPhotos, entityRecord } = useEntityFileContext();
  const { project, editorHandler } = useSwivuBryntumContext();
  const files = [...boardFiles, ...companyCamPhotos];
  const confirm = useConfirm();
  const showNavArrows = files.length > 3;
  const [fileToAnnotate, setFileToAnnotate] = useState<FormattedFile | null>(null);

  // Use the spot incentive hook for "Adjust Points" functionality
  const { shouldShowAdjustPoints, handleAdjustPoints } = useFileSpotIncentive({
    entityRecord,
    project,
    editorHandler
  });

  const handleNextClick = async ({ canScrollNext, scrollNext }: { canScrollNext: boolean; scrollNext: () => void }) => {
    if (!canScrollNext && hasMorePhotos && !isFetchingPhotos) {
      fetchCompanyCamPhotos();
      await new Promise((resolve) => setTimeout(resolve, 50));
    }
    scrollNext();
  };

  return (
    <div className="relative">
      <Carousel
        opts={{
          align: "start",
          slidesToScroll: 5,
          watchDrag: false,
          dragFree: false,
          containScroll: "trimSnaps"
        }}
        className="w-full"
      >
        <CarouselContent className={`gap-4 ml-0`}>
          {files.map((file) => (
            <CarouselItem key={file.id} className="basis-24  flex flex-col pl-0">
              <div
                  className={cn(
                    "size-24 bg-muted rounded-lg overflow-hidden border transition-all duration-300 relative group cursor-pointer",
                    file.isAnnotated
                      ? "border-[#144f94]/40 hover:border-[#0096ff] hover:shadow-lg hover:shadow-[#144f94]/20"
                      : "border-border hover:border-primary/50"
                  )}
                onClick={() => {
                  handleFileOpen(file);
                }}
              >
                <FilePreview file={file} className="w-full h-full object-cover" />

                {/* Stylish Annotated Corner Ribbon - Bottom Left */}
                {file.isAnnotated && (
                  <div className="absolute bottom-0 left-0 overflow-hidden w-16 h-16 pointer-events-none z-10">
                    <div className="absolute -left-10 bottom-0 w-24 text-center transform rotate-45 bg-gradient-to-r from-[#144f94] via-[#0096ff] to-[#144f94] text-white py-1 shadow-lg flex items-center justify-center">
                      <Pencil className="w-3 h-3 -rotate-45" />
                    </div>
                  </div>
                )}

                {/* More options */}
                <div className="z-10 absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <DropdownMenu modal={false}>
                    <DropdownMenuTrigger asChild>
                      <button
                        className="p-1 bg-black/50 text-white rounded hover:bg-black/70 transition-colors"
                        onClick={(e) => {
                          e.stopPropagation();
                        }}
                      >
                        <MoreVertical className="w-3 h-3" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-48">
                      <DropdownMenuItem
                        disabled={!file.canView}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleFileOpen(file);
                        }}
                      >
                        <Eye className="mr-2 h-4 w-4" />
                        View
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        disabled={!file.canView}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleFileDownload(file);
                        }}
                      >
                        <Download className="mr-2 h-4 w-4" />
                        Download
                      </DropdownMenuItem>
                      {isImageFile(file) && (
                        <DropdownMenuItem
                          disabled={file.isCCImage ? false : !file.canEdit}
                          onClick={(e) => {
                            e.stopPropagation();
                            setFileToAnnotate(file);
                          }}
                        >
                          <Pencil className="mr-2 h-4 w-4" />
                          Annotate
                        </DropdownMenuItem>
                      )}
                      {shouldShowAdjustPoints(file) && (
                        <DropdownMenuItem
                          onClick={(e) => {
                            e.stopPropagation();
                            handleAdjustPoints(file);
                          }}
                        >
                          <Award className="mr-2 h-4 w-4" />
                          Adjust Points
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        disabled={!file.canEdit}
                        onClick={async (e) => {
                          e.stopPropagation();
                          if (
                            file.canEdit &&
                            (await confirm({
                              title: "Confirm Delete",
                              description: `Are you sure you want to delete "${file.name}"?`,
                              customActions: ({ confirm, cancel }: { confirm: () => void; cancel: () => void }) => (
                                <div className="space-x-4">
                                  <Button variant="ghost" onClick={cancel}>
                                    Cancel
                                  </Button>
                                  <Button
                                    className="dark:bg-red-900 dark:text-red-200 bg-red-100 text-red-800 hover:dark:bg-red-950 hover:dark:text-red-300 hover:bg-red-200 transition-colors"
                                    onClick={confirm}
                                  >
                                    Delete
                                  </Button>
                                </div>
                              )
                            }))
                          ) {
                            deleteFile(file.id);
                          }
                        }}
                        className="text-destructive focus:text-destructive"
                      >
                        <Trash2 className="mr-2 h-4 w-4" />
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
              <Tooltip>
                <TooltipTrigger>
                  <p className="text-xs text-muted-foreground mt-2 mb-0 truncate">{file.name}</p>
                </TooltipTrigger>
                <TooltipContent>{file.name}</TooltipContent>
              </Tooltip>
            </CarouselItem>
          ))}

          {/* Show skeleton loaders when fetching more photos */}
          {isFetchingPhotos &&
            hasMorePhotos &&
            Array.from({ length: 15 }, (_, index) => index).map((box) => (
              <CarouselItem key={`skeleton-${box}`} className="basis-24 flex flex-col pl-0">
                <div className="space-y-2">
                  <Skeleton className="w-24 h-24 rounded-md shrink-0 bg-muted border" />
                  <Skeleton className="w-12 h-4 rounded-md bg-muted border" />
                </div>
              </CarouselItem>
            ))}
        </CarouselContent>

        {/* Navigation arrows - positioned within content area */}
        {showNavArrows && (
          <>
            <CarouselPrevious className="absolute left-2 top-1/2 -translate-y-1/2 h-8 w-8 bg-background/90 hover:bg-background border shadow-sm z-10" />
            <CarouselNext
              onSwivuCarouselClick={handleNextClick}
              disabled={isFetchingPhotos}
              className="absolute right-2 top-1/2 -translate-y-1/2 h-8 w-8 bg-background/90 hover:bg-background border shadow-sm z-10"
            />
          </>
        )}
      </Carousel>

      {/* Image Annotation Editor */}
      {fileToAnnotate && fileToAnnotate.originalImageUrl && (
        <ImageAnnotationEditor project={project} entityRecord={entityRecord} fileToAnnotate={fileToAnnotate} onClose={() => setFileToAnnotate(null)} />
      )}
    </div>
  );
};
